<script setup lang="ts">
import { TagsInputItem, TagsInputItemDelete, TagsInputItemText } from 'reka-ui';
import { computed, toRef } from 'vue';

import { KaBadge, type KaBadgeColor, KaBadgeRemove } from '@/kasten-components/components/KaBadge';
import { useDataTestid, useSlotText } from '@/kasten-components/composables';
import type { IconOptions, VueClass } from '@/kasten-components/types';
import { cnMerge } from '@/utils/utils.js';

import type { KaComboboxOptionProps } from './KaComboboxOption.vue';

export type KaComboboxTagProps = {
  value: KaComboboxOptionProps;
  label?: string;
  color?: KaBadgeColor;
  removeable?: boolean;
  left?: IconOptions;
  class?: VueClass;
  iconContainerClasses?: VueClass;
  removeButtonClasses?: VueClass;
};

const props = withDefaults(defineProps<KaComboboxTagProps>(), {
  color: 'gray',
  removeable: true,
});

const emit = defineEmits<{
  (e: 'remove:blur', event: FocusEvent): void;
}>();

const slots = defineSlots<{
  default?(props: { label?: string }): any;
  left?(props: Record<string, never>): any;
}>();

const { dataTestid, suffix } = useDataTestid();

const slotTextContent = useSlotText({
  name: 'default',
  slotProps: { label: props.label },
});
const internalLabel = computed(() => props.label || slotTextContent.value);
const value = toRef({ ...props.value });
</script>
<template>
  <TagsInputItem as-child :data-testid="dataTestid" :value="value">
    <KaBadge :class="cnMerge(props.class, 'max-w-full')" :left="props.left">
      <template v-if="'left' in slots" #left>
        <slot name="left" />
      </template>

      <TagsInputItemText class="truncate">
        <slot>{{ props.label }}</slot>
      </TagsInputItemText>

      <template v-if="props.removeable || 'remove' in slots" #remove>
        <TagsInputItemDelete
          as-child
          :data-testid="suffix('remove-button')"
          tabindex="0"
          :aria-labelledby="
            // The KaBadgeRemove component has an aria-label that describes the remove button
            undefined
          "
        >
          <KaBadgeRemove
            :label="internalLabel"
            :color="props.color"
            @remove:blur="emit('remove:blur', $event)"
          />
        </TagsInputItemDelete>
      </template>
    </KaBadge>
  </TagsInputItem>
</template>
